import { makeAutoObservable, runInAction } from 'mobx'

import { AccountApi } from '../api'
import { upsertByGuid } from '../utils/upsertByGuid'
import {
  type Account,
  type DetailedAccount,
  type Institution,
  type Member,
  AccountType,
  AccountSubType,
} from '../types'
import { augmentAccounts } from '../utils/AccountUtils'
import { isFeatureEnabled } from '../utils/UserUtils'

import { GlobalStore } from './GlobalStore'

export class AccountStore {
  globalStore: GlobalStore
  api: AccountApi = new AccountApi('/', '')

  accounts: Account[] = []
  accountsLoaded: boolean = false
  accountsLoading: boolean = false
  institutions: Institution[] = []
  institutionsLoaded: boolean = false
  institutionsLoading: boolean = false
  members: Member[] = []
  membersLoaded: boolean = false
  membersLoading: boolean = false
  uiStore: UiStore

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.uiStore = new UiStore()
    this.api = new AccountApi(globalStore.endpoint, globalStore.sessionToken, globalStore.onError)

    makeAutoObservable(this)
  }

  loadAccountData = async () => {
    await Promise.all([this.loadAccounts(), this.loadMembers(), this.loadInstitutions()])
  }

  get isAccountDataLoaded(): boolean {
    return this.accountsLoaded && this.membersLoaded && this.institutionsLoaded
  }

  get detailedAccounts(): DetailedAccount[] {
    const userFeatures = this.globalStore.userStore.userFeatures
    const excludeExternal = isFeatureEnabled(userFeatures, 'MD_EXCLUDE_EXTERNAL_ACCOUNTS')

    // Hide held account when this flag is toggled and this is the individual accounts widget
    const excludeHeld =
      this.globalStore.appConfig.config.display_only_external_accounts_in_accounts &&
      this.globalStore.appConfig.options.subtype === 'accounts' // Indicates this is an individual widget

    return augmentAccounts(
      this.accounts,
      this.institutions,
      this.members,
      excludeExternal,
      excludeHeld,
    )
  }

  get accountsByTypeMap(): Map<AccountType, DetailedAccount[]> {
    return this.detailedAccounts.reduce((typeMap: Map<AccountType, DetailedAccount[]>, account) => {
      const accountType = account.account_type

      let accounts = typeMap.get(accountType)
      if (!accounts) {
        accounts = []
      }
      accounts.push(account)
      typeMap.set(accountType, accounts)

      return typeMap
    }, new Map<AccountType, DetailedAccount[]>())
  }

  get cashAccounts(): DetailedAccount[] {
    return this.detailedAccounts.filter(
      (account: Account) =>
        account.account_type === AccountType.CASH ||
        account.account_type === AccountType.CHECKING ||
        account.account_type === AccountType.PREPAID ||
        account.account_type === AccountType.SAVINGS ||
        account.account_type === AccountType.CHECKING_LINE_OF_CREDIT,
    )
  }

  get cashBalance(): number {
    return this.cashAccounts.reduce((total, account) => total + (account?.balance || 0), 0)
  }

  get checkingAccounts(): DetailedAccount[] {
    return this.detailedAccounts.filter((a: Account) => a.account_type === AccountType.CHECKING)
  }

  get debtAccounts(): DetailedAccount[] {
    return this.detailedAccounts.filter(
      (account: Account) =>
        account.account_type === AccountType.CREDIT_CARD ||
        account.account_type === AccountType.LOAN ||
        account.account_type === AccountType.MORTGAGE ||
        account.account_type === AccountType.LINE_OF_CREDIT ||
        account.account_type === AccountType.CHECKING_LINE_OF_CREDIT,
    )
  }

  get finstrongAccounts(): DetailedAccount[] {
    const eligibleTypes = new Set([
      AccountType.CHECKING,
      AccountType.SAVINGS,
      AccountType.CREDIT_CARD,
      AccountType.INVESTMENT,
    ])

    return this.detailedAccounts.filter((account) => eligibleTypes.has(account.account_type))
  }

  get investmentAccounts(): DetailedAccount[] {
    return this.detailedAccounts.filter(
      (account: Account) => account.account_type === AccountType.INVESTMENT,
    )
  }

  get manualAccounts(): DetailedAccount[] {
    return this.detailedAccounts.filter((account) => account.is_manual)
  }

  get savingsAccounts(): DetailedAccount[] {
    return this.detailedAccounts.filter((a: Account) => a.account_type === AccountType.SAVINGS)
  }

  get retirementAccounts(): Account[] {
    return this.detailedAccounts.filter(
      (account: Account) =>
        account.account_type === AccountType.INVESTMENT ||
        account.account_type === AccountType.SAVINGS ||
        account.account_type === AccountType.CHECKING ||
        account.account_type === AccountType.CASH ||
        account.account_type === AccountType.PROPERTY ||
        (account.account_type === AccountType.INSURANCE &&
          (account.account_subtype === AccountSubType.WHOLE_LIFE ||
            account.account_subtype === AccountSubType.UNIVERSAL_LIFE ||
            account.account_subtype === AccountSubType.VARIABLE_UNIVERSAL_LIFE)),
    )
  }

  get visibleAccounts(): DetailedAccount[] {
    return this.detailedAccounts.filter((a: Account) => !a.is_hidden && !a.is_closed)
  }

  get visibleCashAccounts(): DetailedAccount[] {
    return this.cashAccounts.filter((account: Account) => !account.is_hidden)
  }

  get visibleCashBalance(): number {
    return this.visibleCashAccounts.reduce((total, account) => total + (account?.balance || 0), 0)
  }

  get visibleCheckingAccounts(): DetailedAccount[] {
    return this.checkingAccounts.filter((account: Account) => !account.is_hidden)
  }

  get visibleDebtAccounts(): DetailedAccount[] {
    return this.debtAccounts.filter((account: Account) => !account.is_hidden)
  }

  get visibleFinstrongAccounts(): DetailedAccount[] {
    return this.finstrongAccounts.filter(
      (account: Account) => !(account.is_closed || account.is_deleted || account.is_hidden),
    )
  }

  get visibleRetirementAccounts(): Account[] {
    return this.retirementAccounts.filter((account: Account) => !account.is_hidden)
  }

  get visibleInvestmentAccounts(): DetailedAccount[] {
    return this.investmentAccounts.filter((account: Account) => !account.is_hidden)
  }

  get visibleSavingsAccounts(): DetailedAccount[] {
    return this.savingsAccounts.filter((account: Account) => !account.is_hidden)
  }

  get savingsAndCheckingAccountsBalance(): number {
    const savingsAndCheckingAccountsList = this.savingsAndCheckingAccounts

    return savingsAndCheckingAccountsList.reduce(
      (total, account) => total + (account?.balance || 0),
      0,
    )
  }

  get savingsAndCheckingAccounts(): Account[] {
    const savingsAndCheckingAccounts = this.accounts.filter(
      (account: Account) =>
        account.account_type === AccountType.SAVINGS ||
        account.account_type === AccountType.CHECKING,
    )

    return savingsAndCheckingAccounts
  }

  setAccounts = (accounts: Account[]) => {
    this.accounts = accounts
    this.accountsLoading = false
    this.accountsLoaded = true
  }

  setMembers = (members: Member[]) => {
    this.members = members
    this.membersLoading = false
    this.membersLoaded = true
  }

  setInstitutions = (institutions: Institution[]) => {
    this.institutions = institutions
    this.institutionsLoading = false
    this.institutionsLoaded = true
  }

  addAccount = async (account: Account) => {
    try {
      const newAccount = await this.api.createAccount(account)

      runInAction(() => {
        this.accounts = upsertByGuid(this.accounts, newAccount)
      })
    } catch (error) {
      console.error('Error occurred while adding an account:', error)
      this.globalStore.captureException(error, { store: 'AccountStore' })
    }
  }

  loadAccounts = async () => {
    if (this.accountsLoading) return

    this.accountsLoading = true
    try {
      const accounts = await this.api.getAccounts()
      this.setAccounts(accounts)
    } catch (error) {
      console.error('Error occurred while loading accounts:', error)
      this.globalStore.captureException(error, { store: 'AccountStore' })
      this.setAccounts([])
    }
  }

  loadInstitutions = async () => {
    if (this.institutionsLoading) return

    this.institutionsLoading = true
    try {
      const institutions = await this.api.getInstitutions()
      this.setInstitutions(institutions)
    } catch (error) {
      console.error('Error occurred while loading institutions:', error)
      this.globalStore.captureException(error, { store: 'AccountStore' })
      this.setInstitutions([])
    }
  }

  loadMembers = async () => {
    if (this.membersLoading) return

    this.membersLoading = true
    try {
      const members = await this.api.getMembers()
      this.setMembers(members)
    } catch (error) {
      console.error('Error occurred while loading members:', error)
      this.globalStore.captureException(error, { store: 'AccountStore' })
      this.setMembers([])
    }
  }

  mergeAccounts = async (accountGuids: string[]) => {
    try {
      const response = await this.api.mergeAccounts(accountGuids)
      // the response from the api is a funky database key here, so this is essentially just getting the first item in the returned object
      // to prevent making multiple api calls here
      const mergedAccount = response[Object.keys(response)[0]]

      runInAction(() => {
        const account2Index = this.accounts.findIndex((account) => account.guid === accountGuids[1])
        this.accounts.splice(account2Index, 1)

        const account1Index = this.accounts.findIndex((account) => account.guid === accountGuids[0])
        this.accounts.splice(account1Index, 1, mergedAccount)
      })
    } catch (error) {
      console.error('Error occurred while merging accounts:', error)
      this.globalStore.captureException(error, { store: 'AccountStore' })
    }
  }

  /**
   * DEPRECATED: Use loadAccountData instead
   */
  refreshAccounts = async () => {
    await Promise.all([this.loadAccounts(), this.loadInstitutions(), this.loadMembers()])
  }

  refreshMembers = async () => {
    try {
      await this.api.refreshAllMembers()
    } catch (error) {
      console.error('Error occurred while refreshing members:', error)
      this.globalStore.captureException(error, { store: 'AccountStore' })
    }
  }

  removeAccount = async (accountGuid: string) => {
    try {
      await this.api.deleteAccount(accountGuid)

      const deletedIndex = this.accounts.findIndex((account) => account.guid === accountGuid)
      this.accounts.splice(deletedIndex, 1)
    } catch (error) {
      console.error('Error occurred while removing an account:', error)
      this.globalStore.captureException(error, { store: 'AccountStore' })
    }
  }

  updateAccount = async (account: Account) => {
    try {
      const updatedAccount = await this.api.updateAccount(account)

      const updatedAccountIndex = this.accounts.findIndex(
        (account) => account.guid === updatedAccount.guid,
      )

      this.accounts.splice(updatedAccountIndex, 1, updatedAccount)
    } catch (error) {
      console.error('Error occurred while updating account:', error)
      this.globalStore.captureException(error, { store: 'AccountStore' })
    }
  }

  updateAccountBalance = (accountGuid: string, balanceChange: number) => {
    const account = this.accounts.find((a) => a.guid === accountGuid)

    if (account) {
      runInAction(() => {
        account.balance = (account.balance || 0) + balanceChange
      })
    }
  }

  updateAccountFilter = async (accountMap: Map<string, boolean>) => {
    try {
      const accountUpdates: Promise<Account | null>[] = []
      for (const [guid, isSelected] of accountMap.entries()) {
        const account = this.accounts.find((acc) => acc.guid === guid)

        if (account && account.is_excluded_from_accounts === isSelected) {
          const updatedPayload = { ...account, is_excluded_from_accounts: !isSelected }
          accountUpdates.push(
            this.api.updateAccount(updatedPayload).catch((error) => {
              console.error('Error occurred while updating account filter:', error)
              this.globalStore.captureException(error, { store: 'AccountStore' })
              return null
            }),
          )
        }
      }

      const results = await Promise.all(accountUpdates)
      const updatedAccounts: Account[] = results.filter((acc): acc is Account => acc !== null)

      const accounts = [...this.accounts]
      for (const updatedAccount of updatedAccounts) {
        const index = accounts.findIndex((acc) => acc.guid === updatedAccount.guid)
        if (index !== -1) {
          accounts.splice(index, 1, updatedAccount)
        }
      }
      this.setAccounts(accounts)
    } catch (error) {
      console.error('Error occurred while updating account filter:', error)
      this.globalStore.captureException(error, { store: 'AccountStore' })
    }
  }
}

// TODO: Move into AccountStore
class UiStore {
  selectedAccount: Account = {} as Account
  selectedMember: Member = {} as Member

  constructor() {
    makeAutoObservable(this)
  }

  setSelectedAccount = (account: Account) => (this.selectedAccount = account)
  setSelectedMember = (member: Member) => (this.selectedMember = member)
}
